import * as React from 'react';
import { Alert, Button, Modal, Textarea } from '@ixicare/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import classnames from 'classnames';
import { Device, DeviceType } from '@ixicare/resources';
import { t } from '~i18n';
import { Spacer } from '~components/Utils/Spacer';
import { humaniseErrors } from '~utils/error';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import { DeviceCommand, sendDeviceCommand } from '~modules/devices/api/deviceCommands';
import styles from '~/components/ActionBar/ActionBar.module.css';

const MAX_CHIPS = 7;
// What a Twig screen can show: letters (accents are dropped on the way), digits and spaces. An Eview speaks
// any text, but needs one.
const MAX_MESSAGE_LENGTH = 139;
const SCREEN_TEXT = /^[\p{Script=Latin}\p{M}0-9 ]*$/u;
// Only an EV-04 from build 2110 (YYMM) speaks a text: the same rule as `eview.can_speak` in
// traccar-ingestion-lambdas, which refuses the others. Firmware reads e.g. "EV-04.8601.2110".
const SPEAKING_FIRMWARE = /^E?V-?0*(\d+)[A-Z]*\.\d+\.(\d{4})/;

const canSpeak = (firmware: unknown) => {
  const match = typeof firmware === 'string' && SPEAKING_FIRMWARE.exec(firmware.trim().toUpperCase());
  return !!match && Number(match[1]) === 4 && Number(match[2]) >= 2110;
};

/** The firmware each Eview last reported, from its settings read-out; undefined when not known yet. */
const reportedFirmware = (devices: DBGWithType[]) =>
  Promise.all(
    devices.map((device) =>
      'uuid' in device && typeof device.uuid === 'string'
        ? Device.getCurrentDeviceSettings(device.uuid)
            .then((settings) => settings.lastConfirmedConfiguration?.firmware)
            .catch(() => undefined)
        : Promise.resolve(undefined)
    )
  );

interface DeviceCommandModalProps {
  devices: DBGWithType[];
  action: DeviceCommand['action'];
  onClose: () => void;
}

export function DeviceCommandModal({ devices, action, onClose }: DeviceCommandModalProps) {
  const [text, setText] = React.useState('');
  const shownDevices = devices.slice(0, MAX_CHIPS);
  const overflow = devices.length - shownDevices.length;
  const isMessage = action === 'message';
  const eviews = isMessage ? devices.filter((d) => d.type === DeviceType.EVIEW) : [];
  const firmware = useQuery({
    queryKey: ['device-command', 'eview-firmware', eviews.map((d) => d.id)],
    queryFn: () => reportedFirmware(eviews),
    enabled: eviews.length > 0,
  });
  const silent = eviews.filter((_, index) => !canSpeak(firmware.data?.[index]));
  const recipients = isMessage ? devices.filter((d) => !silent.includes(d)) : devices;
  const checkingFirmware = eviews.length > 0 && firmware.isPending;
  const anyTwig = recipients.some((d) => d.type === DeviceType.TWIG);
  const allEview = devices.every((d) => d.type === DeviceType.EVIEW);
  const textIsValid = !anyTwig || SCREEN_TEXT.test(text);
  const textIsMissing = !anyTwig && !text.trim();
  const explanation =
    isMessage && allEview ? t('device.command.message.explanationSpoken') : t(`device.command.${action}.explanation`);

  const mutation = useMutation({
    mutationFn: async () => {
      const command: DeviceCommand = isMessage ? { action, text: text.trim() } : { action };
      const results = await Promise.allSettled(recipients.map((d) => sendDeviceCommand(Number(d.id), command)));
      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (failures.length) throw failures[0].reason;
    },
    onSuccess: onClose,
  });

  return (
    <Modal onClose={onClose} style={{ maxWidth: 450 }}>
      <h3 className="text-center mb-10">{t(`device.command.${action}.action`)}</h3>
      <ul className={styles.chips}>
        {shownDevices.map((device) => (
          <li className={classnames('text-center', 'mb-10', styles.chip)} key={device.macAddress}>
            {device.macAddress} - {device.description}
          </li>
        ))}
        {overflow > 0 && <li className={classnames('text-center', styles.chip)}>+{overflow}</li>}
      </ul>
      <p className="text-muted text-center">{explanation}</p>
      {isMessage && (
        <Textarea
          id="device-command-message"
          label={t('device.command.message.text')}
          value={text}
          rows={3}
          maxLength={MAX_MESSAGE_LENGTH}
          onValueChange={setText}
        />
      )}
      {isMessage && !textIsValid && <Alert type="error">{t('device.command.message.invalid')}</Alert>}
      {!checkingFirmware && silent.length > 0 && (
        <Alert type="warning">
          {t('device.command.message.cannotSpeak', { devices: silent.map((d) => d.macAddress).join(', ') })}
        </Alert>
      )}
      {mutation.error && <Alert type="error">{humaniseErrors(mutation.error as Error).title}</Alert>}
      <Spacer size={32} />
      <Button
        className="mb-15"
        isBlock
        loading={mutation.isPending || checkingFirmware}
        disabled={isMessage && (!textIsValid || textIsMissing || recipients.length === 0)}
        type="button"
        onClick={() => mutation.mutate()}
      >
        {t(`device.command.${action}.confirm`)}
      </Button>
      <Button onClick={() => onClose()} disabled={mutation.isPending} isBlock buttonType="link">
        {t('global.cancel')}
      </Button>
    </Modal>
  );
}
